/**
 * "PAID IN TOTAL" - EVERYTHING AN AUTHOR HAS BEEN PAID, IN ONE FIGURE (the owner, 2026-10-05, on the
 * earlier months live: "add at payments tab something like a new card total paid/earned", then "one
 * combined 'Paid in total'"). Two sums, each exact or not drawn:
 *
 *  - BEFORE: the earlier months the reports mark `paid`, each by its `totalMinor`. A paid month's
 *    total already holds what was carried into it from the months before, and a carried month was not
 *    paid, so every dollar the old payments sent is counted once. A month marked `carried` or
 *    `unknown` adds nothing: what was still owed when these figures began is the opening balance
 *    ("From earlier"), paid out by the payouts here - never by an earlier month.
 *  - HERE: the monthly payouts with a `paidAt`, by the rules of ./paid-to-date.ts (the whole list,
 *    paid money only, one currency) - but a total of 0 is a sum here, not a reason to leave out.
 *
 * Not exact, not drawn - the caller falls back to "Paid to date" (the payouts here alone): a paid
 * earlier month whose total the report did not state (null), a paid month or a paid payout in another
 * currency than the payments view, or no payout history served. Nothing paid on either side is no
 * card at all, as "Paid to date" has none: "$0.00 paid" over an author with years of work reads as a
 * fault. The figure is display only - nothing here is a balance and nothing is sent anywhere.
 * Integer minor units, added and never divided (../../features/cabinet/components/money.tsx, rule 2).
 */
import type { OperationResult, PaymentsView } from '../../features/cabinet/api';

type EarlierMonthsAnswer = OperationResult<'listEarlierMonths'>;

export interface PaidInTotal {
  /** Before + here. */
  readonly totalMinor: number;
  /** The earlier months marked paid. */
  readonly beforeMinor: number;
  /** The monthly payouts here that were paid. */
  readonly hereMinor: number;
  /** The first and the last earlier month counted (`YYYY-MM`); absent when none was. */
  readonly beforeRange?: { readonly from: string; readonly to: string };
}

export function paidInTotal(
  payments: PaymentsView,
  earlier: EarlierMonthsAnswer,
): PaidInTotal | undefined {
  const here = paidHereMinor(payments);
  if (here === undefined) {
    return undefined;
  }
  let before = 0;
  const periods: string[] = [];
  for (const month of earlier.months) {
    if (month.payoutStatus !== 'paid') {
      continue;
    }
    if (month.currency !== payments.currency || month.totalMinor === null) {
      return undefined;
    }
    before += month.totalMinor;
    periods.push(month.period);
  }
  const total = before + here;
  if (total === 0) {
    return undefined;
  }
  periods.sort();
  const from = periods[0];
  const to = periods[periods.length - 1];
  return {
    totalMinor: total,
    beforeMinor: before,
    hereMinor: here,
    ...(from === undefined || to === undefined ? {} : { beforeRange: { from, to } }),
  };
}

/** The paid payouts here, 0 when none was paid yet; `undefined` when that cannot be said exactly. */
function paidHereMinor(payments: PaymentsView): number | undefined {
  const payouts = payments.payouts;
  if (payouts === undefined) {
    return undefined;
  }
  let total = 0;
  for (const payout of payouts) {
    if (payout.paidAt === undefined || payout.paidAt === null) {
      continue;
    }
    if (
      payout.currency !== payments.currency ||
      payout.amountMinor === undefined ||
      payout.amountMinor === null
    ) {
      return undefined;
    }
    total += payout.amountMinor;
  }
  return total;
}
